import * as UI from "@iyk/ui"
import * as React from "react"
import * as Dialog from "../../lib/ui/dialog.tsx"

import { toSentenceCase } from "@iyk/string"
import { Link } from "@remix-run/react"
import { HOW_TO_TAP_URL } from "../../lib/constants.ts"
import { useIykUser } from "../../lib/hooks/use-iyk-user.ts"
import { useLang } from "../../lib/lang/use-lang.ts"
import { useLoginDialog } from "../../lib/ui/login-dialog-provider.tsx"
import { formatNumber } from "../../lib/utils/number.ts"
import { classForHeading } from "./points-heading.tsx"

// #region Root

const Root = ({ children, ...props }: React.ComponentPropsWithoutRef<typeof Dialog.Root>) => {
  return <Dialog.Root {...props}>{children}</Dialog.Root>
}

// #endregion

// #region Trigger

const Trigger = ({ children, ...props }: React.ComponentProps<typeof Dialog.Trigger>) => (
  <Dialog.Trigger
    aria-label="Open Points Dialog"
    className={UI.classForButton({ kind: "ghost", size: "sm" })}
    {...props}
  >
    {children ?? <UI.Icon.ChevronRight />}
  </Dialog.Trigger>
)

// #endregion

// #region Content

const Content = ({
  children,
  headerHeight = 186,
}: {
  children: React.ReactNode
  headerHeight?: number
}) => {
  const lang = useLang()
  return (
    <Dialog.Content
      onOpenAutoFocus={(e) => {
        e.preventDefault()
      }}
      // We need to override the padding since the first section has no padding, which is outside of our DS.
      className="!p-0 w-full max-h-[calc(100dvh-var(--dialog-margin-top))] [--dialog-margin-top:40px]"
      style={{
        "--dialog-header-height": `${headerHeight}px`,
      }}
    >
      <div className="sr-only">
        <Dialog.Title>{lang.terms.POINTS}</Dialog.Title>
        <Dialog.Description>{lang.terms.POINTS_DIALOG_DESCRIPTION}</Dialog.Description>
      </div>
      {children}
    </Dialog.Content>
  )
}

// #endregion

// #region ContentSections

const ContentSections = ({
  className,
  sections,
  children,
  ...props
}: React.ComponentProps<"section"> & { sections: PointsDialogSection[] }) => {
  const lang = useLang()
  return (
    <ContentContainerBase data-ui="Scrollable content" {...props}>
      <span className={classForHeading}>{lang.terms.POINTS_DIALOG_DESCRIPTION}</span>
      <ContentSectionsBase sections={sections} />
      {children}
    </ContentContainerBase>
  )
}

const ContentSectionsBase = ({ sections }: { sections: PointsDialogSection[] }) => (
  <div className="flex flex-col gap-4">
    {sections.map((section) => (
      <Section key={section.id} section={section} />
    ))}
  </div>
)

const Section = ({ section }: { section: PointsDialogSection }) => (
  <div className="flex flex-col gap-2 [&:not(:last-child)]:border-b [&:not(:last-child)]:border-dotted [&:not(:last-child)]:border-gray-6 [&:not(:last-child)]:pb-4">
    <h3 className={classForHeading}>{section.title}</h3>
    <div className="flex flex-col gap-3">
      <p className="text-sm">{section.description}</p>
      {section.link ? (
        section.link.href ? (
          <UI.Link
            href={section.link.href}
            className="text-xs w-fit"
            target="_blank"
            rel="noreferrer"
          >
            {section.link.text}
          </UI.Link>
        ) : (
          section.link.to && (
            <Link to={section.link.to} className={UI.classForLink({ className: "text-xs w-fit" })}>
              {section.link.text}
            </Link>
          )
        )
      ) : null}
    </div>
  </div>
)

const ContentContainerBase = ({
  children,
  className,
  ...props
}: React.ComponentProps<"section">) => {
  return (
    <section
      className={UI.cx(
        "flex flex-col gap-10 px-4 py-6 overflow-auto max-h-[calc(100dvh-var(--dialog-margin-top)-var(--dialog-header-height))]",
        className,
      )}
      {...props}
    >
      {children}
    </section>
  )
}

// #endregion

// #region ContentHeader

const ContentHeader = ({
  userPointsBalance,
  team,
  onClose,
  ...props
}: {
  userPointsBalance: number
  team: {
    name: string
    urlSlug: string
    iconUrl: string
  }
  onClose?: () => void
} & React.ComponentProps<"section">) => {
  const { iykUser } = useIykUser()
  const lang = useLang()

  const { showLoginDialog } = useLoginDialog()

  return (
    <ContentHeaderBase {...props}>
      <UI.Icon.Points className="bg-points" />
      <div className="flex flex-col gap-3">
        <span className="text-[11px] leading-[14px] font-departure-mono uppercase">
          {team.name}
        </span>
        <div className="flex items-center gap-3">
          <UI.Avatar src={team.iconUrl} className="size-7" variant="square" />
          {iykUser ? (
            <div className="flex items-baseline gap-2 font-departure-mono uppercase">
              <span className="text-[32px] leading-normal">{formatNumber(userPointsBalance)}</span>
              <span className="text-[11px] leading-[14px] uppercase">{lang.terms.POINTS}</span>
            </div>
          ) : (
            <>
              <span className="text-sm flex-1">
                {toSentenceCase(lang.terms.LOGIN_TO_VIEW_POINTS)}
              </span>
              <UI.Button
                size="sm"
                kind="outline"
                type="button"
                onClick={() => {
                  onClose?.()
                  showLoginDialog()
                }}
              >
                {toSentenceCase(lang.terms.LOGIN)}
              </UI.Button>
            </>
          )}
        </div>
      </div>
    </ContentHeaderBase>
  )
}

const ContentHeaderBase = ({ children, className, ...props }: React.ComponentProps<"section">) => {
  return (
    <section
      className={UI.cx("flex flex-col gap-16 bg-points-muted p-4 md:rounded-t-xl", className)}
      {...props}
    >
      {children}
    </section>
  )
}

// #endregion

// #region Default points dialog sections

type CommonPointsDialogSectionsParams = {
  lang: ReturnType<typeof useLang>
  linksVisibility?:
    | {
        shop: string | false
        referrals: boolean
        claim: boolean
      }
    | false
}

const commonPointsDialogSections = (params: CommonPointsDialogSectionsParams) => {
  const {
    lang,
    linksVisibility = {
      shop: "",
      referrals: true,
      claim: true,
    },
  } = params
  return [
    {
      id: "shop",
      title: toSentenceCase(lang.terms.SHOP),
      description: toSentenceCase(lang.terms.POINTS_DIALOG_SHOP_DESCRIPTION),
      link:
        linksVisibility && linksVisibility.shop
          ? {
              text: toSentenceCase(lang.terms.SHOP_NOW),
              to: `/creators/${linksVisibility.shop}?filter=shop`,
            }
          : undefined,
    },
    {
      id: "referrals",
      title: toSentenceCase(lang.terms.REFERRALS),
      description: toSentenceCase(lang.terms.POINTS_DIALOG_REFERRALS_DESCRIPTION),
      link:
        linksVisibility && linksVisibility.referrals
          ? {
              text: toSentenceCase(lang.terms.VIEW_MY_ITEMS),
              to: "/profile",
            }
          : undefined,
    },
    {
      id: "claim",
      title: toSentenceCase(lang.terms.CLAIM),
      description: toSentenceCase(lang.terms.POINTS_DIALOG_CLAIM_DESCRIPTION),
      link:
        linksVisibility && linksVisibility.claim
          ? {
              text: toSentenceCase(lang.terms.LEARN_HOW_TO_TAP),
              href: HOW_TO_TAP_URL,
            }
          : undefined,
    },
    {
      id: "events",
      title: toSentenceCase(lang.terms.EVENTS),
      description: toSentenceCase(lang.terms.POINTS_DIALOG_EVENTS_DESCRIPTION),
    },
  ]
}

// #endregion

export type PointsDialogSection = {
  id: string
  title: string
  description: string
  link?: {
    text: string
    to?: string
    href?: string
  }
}

export const PointsDialog = {
  Root,
  Trigger,
  Content,
  ContentContainerBase,
  ContentHeader,
  ContentHeaderBase,
  ContentSections,
  ContentSectionsBase,
  commonPointsDialogSections,
}
